import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useGetActivityDataQuery } from 'features/pricing/pricingSlice';
import { initializeForEdit } from '../createActivityAndPricingStateSlice';
import { ActivityItemPricingType } from '../../PricingManagementConsts';
import { mapServerPricingToStepFormat, mapServerRulesToStepFormat } from '../utils/editMappingUtils';

const useGetActivityData = ({ activityItemId, open, enabled, prefill }) => {
    const dispatch = useDispatch();
    const initializedRef = useRef(false);
    const initializedActivityIdRef = useRef(null);

    const { data: activityData, isLoading } = useGetActivityDataQuery(
        activityItemId,
        { 
            skip: !activityItemId || !open || !enabled,
            refetchOnMountOrArgChange: true
        }
    );

    const serverPricing = activityData?.pricing || [];
    const serverMaterialPrices = activityData?.materialPrices || [];
    const serverPreferredVendors = activityData?.preferredVendors || [];
    const activityMaterials = activityData?.materials || [];
    const serverRules = activityData?.rules || [];
    const ruleParamTypes = activityData?.ruleParamTypes || [];
    const activityItem = activityData?.activityItem;

    const activityParamTypes = activityItem?.id
        ? ruleParamTypes.filter((rule) => rule.activityId === activityItem.id)
        : [];

    const isDataLoaded = !enabled || !isLoading;

    useEffect(() => {
        if (!enabled || !open || !activityItemId || !isDataLoaded || !activityData || !activityItem) {
            return;
        }

        if (initializedRef.current && initializedActivityIdRef.current === activityItem.id) {
            return;
        }

        const activityDetails = {
            ...activityItem,
            paramTypes: activityParamTypes,
            pricingType: activityItem.pricingType || ActivityItemPricingType.SIMPLE,
        };

        const mappedMaterials = activityMaterials.map(m => ({
            id: m.id,
            materialName: m.materialName,
            unitOfMeasure: m.unitOfMeasure || ''
        }));

        const pricingItems = mapServerPricingToStepFormat(serverPricing, serverMaterialPrices, mappedMaterials, serverPreferredVendors);
        const rulesList = mapServerRulesToStepFormat(serverRules);

        dispatch(
            initializeForEdit({
                editActivityId: activityItem.id,
                activityDetails,
                pricingItems,
                rulesList,
                prefill,
            })
        );
        initializedRef.current = true;
        initializedActivityIdRef.current = activityItem.id;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, open, activityItemId, isDataLoaded, activityData, activityItem]);

    useEffect(() => {
        if (!open) {
            initializedRef.current = false;
        }
    }, [open]);

    return {
        isDataLoaded,
        serverPricing,
        serverRules,
    };
};

export default useGetActivityData;
